import React, { useEffect, useState } from "react";
import styles from "./JobDetails.module.css";
import logo from "../../assets/logo.png";
import chevron from "../../assets/chevron-down.png";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { apiEndpoint } from "../../DummyData/DUMMYDATA";
import { useTranslation } from "../../I18n/Languages/LanguageContext";
import LanguageSwitcher from "../../components/LanguageSwitcher/LanguageSwitcher";
import {
  hasValidContent,
  HTMLContent,
} from "../../utils/ContentUtils";
import CircularIndeterminate from "../../components/Spinner/Spinner";

const JobDetails = () => {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [jobDetails, setJobDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isWorkForce, setIsWorkForce] = useState(null);

  const jobId = queryParams.get("jobId");

  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiEndpoint}/job-listings/${jobId}`);
      setJobDetails(response.data);
    } catch (error) {
      console.error(t("error_fetching_job_details"), error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobId) {
      fetchJobDetails();
    }
  }, [jobId]);

  const job = loading ? {} : jobDetails?.data?.[0] ?? {};
  const workingPositionId = job.workingPosition?.id;

  useEffect(() => {
    const fetchFormModel = async () => {
      if (!workingPositionId) return;

      try {
        const response = await axios.get(
          `${apiEndpoint}/working-positions/${workingPositionId}`
        );

        if (response.data.data && response.data.data.length > 0) {
          const workingPositionData = response.data.data[0];
          setIsWorkForce(workingPositionData.isWorkforce);
        } else {
          console.log("No data found in response");
          setIsWorkForce(null);
        }
      } catch (error) {
        console.error(t("error_fetching_form_model"), error);
        setIsWorkForce(null);
      }
    };

    fetchFormModel();
  }, [workingPositionId, t]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.subContainer}>
          <div className={styles.logoSection}>
            <div>
              <img src={logo} alt="Logo" className={styles.logo} />
            </div>
            <div
              style={{
                position: "absolute",
                top: window.innerWidth <= 768 ? "2px" : "20px",
                right: window.innerWidth <= 768 ? "2px" : "20px",
              }}
            >
              <LanguageSwitcher />
            </div>
          </div>
          <div className={styles.divider}></div>

          <div className={styles.loadingContainer}>
            <CircularIndeterminate size={50} />
          </div>
        </div>
      </div>
    );
  }

  const reportsToPosition = job.reportsToPosition?.workingPosition;
  const applicationDeadline = job.applicationDeadline;
  const jobList = job.jobList;

  const getLocalizedValue = (obj, field) => {
    if (!obj) return "";
    return language === "en" ? obj[`${field}En`] || obj[field] : obj[field];
  };

  const jobTitle = getLocalizedValue(job, "jobTitle");

  const skills =
    language === "en"
      ? job.skillsEn || job.skillsAndAbilitiesEn
      : job.skillsAndAbilities;

  const languageRequirements =
    language === "en"
      ? job.languageRequirementsEn || job.languageRequirements
      : job.languageRequirements;

  const workExperience =
    language === "en"
      ? job.workExperienceEn || job.workExperience
      : job.workExperience;

  const customEducation =
    language === "en"
      ? job.customEducationEn || job.customEducation
      : job.customEducation;

  const alternativeEducation =
    language === "en"
      ? job.alternativeEducationEn || job.alternativeEducation
      : job.alternativeEducation;

  const educationRaw = customEducation || alternativeEducation || "";

  const keyResponsibilities =
    language === "en"
      ? job.keyResponsibilitiesEn || job.keyResponsibilities
      : job.keyResponsibilities;

  const positionPurpose =
    language === "en"
      ? job.positionPurposeEn || job.positionPurpose
      : job.positionPurpose;

  const closingDateFormat =
    applicationDeadline && applicationDeadline !== "null"
      ? (() => {
          const date = new Date(applicationDeadline);
          if (isNaN(date)) return "N/A";

          if (language === "al") {
            const monthsAlbanian = [
              "Janar",
              "Shkurt",
              "Mars",
              "Prill",
              "Maj",
              "Qershor",
              "Korrik",
              "Gusht",
              "Shtator",
              "Tetor",
              "Nëntor",
              "Dhjetor",
            ];
            const day = date.getDate();
            const month = monthsAlbanian[date.getMonth()];
            const year = date.getFullYear();
            return `${day} ${month} ${year}`;
          } else {
            return date.toLocaleDateString("en-US", {
              day: "numeric",
              month: "long",
              year: "numeric",
            });
          }
        })()
      : "N/A";

  const cleanString = (str) =>
    str && str.trim() !== "" && str.trim().toLowerCase() !== "null"
      ? str.trim().replace(/^"|"$/g, "")
      : "";

  const reportsToPositionFormat = cleanString(reportsToPosition);

  return (
    <div className={styles.container}>
      <div className={styles.subContainer}>
        <div className={styles.logoSection}>
          <div>
            <img src={logo} alt="Logo" className={styles.logo} />
          </div>
          <div
            style={{
              position: "absolute",
              top: window.innerWidth <= 768 ? "2px" : "20px",
              right: window.innerWidth <= 768 ? "2px" : "20px",
            }}
          >
            <LanguageSwitcher />
          </div>
        </div>
        <div className={styles.divider}></div>

        <div className={styles.mainContent}>
          <div className={styles.jobHeader}>
            <div className={styles.breadcrumb}>
              <span className={styles.breadcrumbText}>{t("work_place")}</span>
            </div>
            <div className={styles.titleSection}>
              <h1 className={styles.jobTitle}>{jobTitle}</h1>
              <button
                className={styles.applyBtnBottom}
                onClick={() =>
                  navigate("/ApplicationForm", {
                    state: { jobTitle, jobList, isWorkForce, jobId },
                  })
                }
              >
                {t("apply")}
                <img src={chevron} alt="chevron" className={styles.chevron} />
              </button>
            </div>
          </div>

          <div className={styles.jobDescription}>
            <p className={styles.introText}>
              {t("job_description_intro", {
                jobTitle,
                reportsToPositionFormat: reportsToPositionFormat || "",
              })}
            </p>

            <div className={styles.deadline}>
              <strong>
                {t("application_deadline")} {closingDateFormat}
              </strong>
            </div>

            <div className={styles.divider}></div>

            <h3 className={styles.sectionTitle}>
              {t("position_requirements_detail")}
            </h3>

            {hasValidContent(positionPurpose) && (
              <HTMLContent content={positionPurpose} />
            )}

            {hasValidContent(keyResponsibilities) && (
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  {t("main_duties_responsibilities")}
                </h3>

                <HTMLContent content={keyResponsibilities} />
              </div>
            )}

            <div className={styles.section}>
              {hasValidContent(educationRaw) && (
                <div className={styles.subsection}>
                  <h4 className={styles.subsectionTitle}>
                    {t("education_detail")}
                  </h4>
                  <HTMLContent content={educationRaw} />
                </div>
              )}

              {hasValidContent(workExperience) && (
                <div className={styles.subsection}>
                  <h4 className={styles.subsectionTitle}>
                    {t("work_experience_detail")}
                  </h4>
                  <HTMLContent content={workExperience} />
                </div>
              )}

              {hasValidContent(languageRequirements) && (
                <div className={styles.subsection}>
                  <h4 className={styles.subsectionTitle}>
                    {t("language_requirements_detail")}
                  </h4>
                  <HTMLContent content={languageRequirements} />
                </div>
              )}

              {hasValidContent(skills) && (
                <div className={styles.subsection}>
                  <h4 className={styles.subsectionTitle}>
                    {t("skills_competencies_detail")}
                  </h4>
                  <HTMLContent content={skills} />
                </div>
              )}
            </div>

            <div className={styles.divider}></div>

            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>
                {t("application_procedure_detail")}
              </h3>
              <p className={styles.procedureText}>
                {t("application_procedure_text")}
              </p>

              <button
                className={styles.applyBtnBottom}
                onClick={() =>
                  navigate("/ApplicationForm", {
                    state: { jobTitle, jobList, isWorkForce, jobId },
                  })
                }
              >
                {t("apply")}
                <img src={chevron} alt="chevron" className={styles.chevron} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetails;
